<template>
  <div class="dashboard-driver-distribution">
    <!-- Filter bar — both filters govern BOTH widgets (top list + distribution), so they sit at the top
         of the card, above the two columns, each with a label + a help tooltip. -->
    <div class="dist-filterbar">
      <div class="dist-filter">
        <span class="dist-filter-label">
          {{ $t('org-dashboard.filter-charge-type') }}
          <v-tooltip bottom max-width="280">
            <template v-slot:activator="{ on }">
              <v-icon x-small class="dist-filter-help" v-on="on">mdi-help-circle-outline</v-icon>
            </template>
            <span>{{ $t('org-dashboard.filter-charge-type-help') }}</span>
          </v-tooltip>
        </span>
        <v-btn-toggle :value="segment" mandatory dense class="dist-seg-toggle"
                      @change="$emit('update:segment', $event)">
          <v-btn x-small value="all">{{ $t('org-dashboard.segment-all') }}</v-btn>
          <v-btn x-small value="home">{{ $t('org-dashboard.segment-home') }}</v-btn>
          <v-btn x-small value="enterprise">{{ $t('org-dashboard.segment-enterprise') }}</v-btn>
          <v-btn x-small value="roaming">{{ $t('org-dashboard.segment-roaming') }}</v-btn>
        </v-btn-toggle>
      </div>

      <div class="dist-filter">
        <span class="dist-filter-label">
          {{ $t('org-dashboard.filter-measure') }}
          <v-tooltip bottom max-width="280">
            <template v-slot:activator="{ on }">
              <v-icon x-small class="dist-filter-help" v-on="on">mdi-help-circle-outline</v-icon>
            </template>
            <span>{{ $t('org-dashboard.filter-measure-help') }}</span>
          </v-tooltip>
        </span>
        <v-btn-toggle v-model="metric" mandatory dense class="dist-metric-toggle">
          <v-btn x-small value="energy">{{ $t('org-dashboard.dist-energy') }}</v-btn>
          <v-btn x-small value="sessions">{{ $t('org-dashboard.dist-sessions') }}</v-btn>
          <v-btn x-small value="cost">{{ $t('org-dashboard.dist-cost') }}</v-btn>
          <v-btn x-small value="costPerKwh">{{ $t('org-dashboard.dist-cost-per-kwh') }}</v-btn>
        </v-btn-toggle>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="dist-toolbar">
      <h3 class="dist-title">
        <v-icon small color="#EF6C00">mdi-account-group</v-icon>
        {{ $t('org-dashboard.top-drivers') }}
        <span class="driver-search-wrapper ml-3">
          <v-text-field
              v-model="driverSearch"
              :placeholder="$t('search')"
              dense
              hide-details
              solo
              flat
              clearable
              prepend-inner-icon="mdi-magnify"
              class="driver-search-field"
          />
        </span>
      </h3>
      <h3 class="dist-title">
        <v-icon small color="#8B5CF6">mdi-chart-box-outline</v-icon>
        Distribution
      </h3>
    </div>

    <div class="dist-columns">
      <!-- ═══ LEFT COLUMN — Top Conducteurs only ═══ -->
      <div class="dist-col-left">
        <div class="driver-list">
          <div
            v-for="(driver, i) in filteredDrivers"
            :key="i"
            :id="'driver-' + driver.memberId"
            class="driver-item"
            :class="{
              'driver-item--clickable': !!driver.memberId,
              'driver-item--selected': selectedDriverId === driver.memberId,
            }"
            @mouseenter="onDriverHover(driver)"
            @mouseleave="onDriverHoverOut()"
            @click="onDriverOpen(driver)"
          >
            <div class="driver-item-header">
              <div class="driver-item-info">
                <div class="driver-item-name">
                  {{ driver.name }}
                  <v-tooltip v-if="driver.hasHomeCharger" bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" x-small :color="homeChargerIcon(driver).color" class="ml-1">{{ homeChargerIcon(driver).icon }}</v-icon>
                    </template>
                    <span>{{ homeChargerLabel(driver.homeChargerType) }}</span>
                  </v-tooltip>
                </div>
                <div class="driver-item-email">{{ driver.email }}</div>
              </div>
              <div class="driver-item-metrics">
                <div class="driver-item-value">{{ formatMetricValue(driver) }}</div>
                <div class="driver-item-badges">
                  <span v-if="deltaVsAvg(driver) != null" class="driver-badge driver-badge--delta" :class="getDeltaClass(deltaVsAvg(driver))">
                    {{ $t('org-dashboard.vs-fleet-avg', { value: formatDelta(deltaVsAvg(driver)) }) }}
                  </span>
                  <span v-if="driver.flag === 'high_usage'" class="driver-badge driver-badge--flag">
                    {{ $t('org-dashboard.high-usage') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="driver-bar-bg">
              <template v-if="driver.segments && driver.segments.length && metric !== 'costPerKwh'">
                <div
                  v-for="seg in getSegmentBars(driver)"
                  :key="seg.name"
                  class="driver-bar-segment"
                  :style="{ width: seg.percent + '%', backgroundColor: seg.color }"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <div class="driver-bar-segment-hit" v-on="on"></div>
                    </template>
                    <span>{{ segmentLabel(seg.name) }} — {{ seg.value }}</span>
                  </v-tooltip>
                </div>
              </template>
              <div v-else class="driver-bar-fill" :style="{width: getBarPercent(driver) + '%'}"></div>
            </div>
          </div>
          <div v-if="!filteredDrivers || !filteredDrivers.length" class="driver-empty">--</div>
        </div>
      </div>

      <!-- ═══ RIGHT COLUMN — Distribution ═══ -->
      <div class="dist-col-right">
        <!-- Loading -->
        <div v-if="distLoading && !distData" class="dist-loading">
          <v-progress-circular indeterminate color="#8B5CF6" size="28" />
        </div>

        <!-- Empty state -->
        <div v-else-if="!distData || !stats || stats.count === 0" class="dist-empty">
          {{ $t('org-dashboard.dist-no-drivers') }}
        </div>

        <div v-else class="dist-chart-area">
          <!-- Loading overlay -->
          <v-overlay :value="distLoading" absolute color="white" opacity="0.5" z-index="2">
            <v-progress-circular indeterminate color="#8B5CF6" size="24" />
          </v-overlay>

          <!-- Truncation banner -->
          <div v-if="distData.truncated" class="dist-banner dist-banner--info mb-3">
            <v-icon x-small color="#1d4ed8" class="mr-1">mdi-information-outline</v-icon>
            {{ $t('org-dashboard.dist-truncated', { metric: metricLabel, count: distData.truncatedDocs || 0 }) }}
          </div>

          <!-- KPI bandeau -->
          <transition name="dist-fade" mode="out-in">
            <div class="dist-kpi-row" :key="metric">
              <div v-for="kpi in kpiItems" :key="kpi.label" class="dist-kpi">
                <div class="dist-kpi-value">{{ kpi.value }}</div>
                <div class="dist-kpi-label">{{ kpi.label }}</div>
              </div>
            </div>
          </transition>

          <!-- Boxplot + Axis + Scatter (unified visualization) -->
          <div class="dist-boxplot-header">
            <span class="dist-boxplot-title">Boxplot</span>
            <span v-if="outlierCount > 0" class="dist-outlier-warning">
              <v-icon x-small color="#DC2626" class="mr-1">mdi-alert</v-icon>
              {{ $t('org-dashboard.dist-outlier-warning', { count: outlierCount }) }}
            </span>
          </div>

          <svg class="dist-combined-svg" :viewBox="`0 0 ${svgWidth} ${combinedSvgHeight}`" preserveAspectRatio="xMidYMid meet">
            <!-- ── Boxplot ── -->
            <line :x1="xScale(boxplot.whiskerLow)" y1="25" :x2="xScale(boxplot.whiskerHigh)" y2="25" stroke="#d1d5db" stroke-width="1.5" />
            <line :x1="xScale(boxplot.whiskerLow)" y1="13" :x2="xScale(boxplot.whiskerLow)" y2="37" stroke="#9ca3af" stroke-width="1.5" />
            <line :x1="xScale(boxplot.whiskerHigh)" y1="13" :x2="xScale(boxplot.whiskerHigh)" y2="37" stroke="#9ca3af" stroke-width="1.5" />
            <rect :x="xScale(boxplot.q1)" y="9" :width="Math.max(xScale(boxplot.q3) - xScale(boxplot.q1), 2)" height="32" rx="3"
                  fill="#8B5CF6" opacity="0.2" stroke="#8B5CF6" stroke-width="1" />
            <line :x1="xScale(boxplot.median)" y1="7" :x2="xScale(boxplot.median)" y2="43" stroke="#8B5CF6" stroke-width="2.5" />
            <polygon :points="meanDiamondPoints" fill="#3B82F6" />
            <!-- Outliers (clamped to axis boundary) -->
            <g v-for="(outlier, oi) in boxplotOutliers" :key="'o'+oi">
              <circle :cx="clampX(outlier.value)" cy="25" r="4" fill="#EF4444" stroke="#fff" stroke-width="1" />
              <title>{{ outlier.label }} — {{ formatValue(outlier.value) }} {{ metricUnit }}</title>
            </g>
            <!-- Selected driver marker -->
            <g v-if="selectedDriverValue != null">
              <line :x1="clampX(selectedDriverValue)" y1="1" :x2="clampX(selectedDriverValue)" y2="49" stroke="#FF5722" stroke-width="2" stroke-dasharray="3,2" />
              <polygon :points="selectedDriverArrowPoints" fill="#FF5722" />
            </g>

            <!-- ── Axis ── -->
            <line :x1="axisTicks.length ? axisTicks[0].trueX : svgPadding" y1="54"
                  :x2="axisTicks.length ? axisTicks[axisTicks.length - 1].trueX : (svgWidth - svgPadding)" y2="54"
                  stroke="#d1d5db" stroke-width="1" />
            <g v-for="tick in axisTicks" :key="'ax-'+tick.key">
              <line :x1="tick.trueX" y1="50" :x2="tick.trueX" y2="58" stroke="#9ca3af" stroke-width="1" />
              <line v-if="Math.abs(tick.x - tick.trueX) > 4" :x1="tick.trueX" y1="58" :x2="tick.x" y2="66" stroke="#9ca3af" stroke-width="0.75" />
              <text :x="tick.x" y="72" text-anchor="middle" :fill="tick.valueColor" font-size="10" font-weight="700">{{ tick.text }}</text>
              <text :x="tick.x" y="83" text-anchor="middle" fill="#9ca3af" font-size="7.5" font-weight="600">{{ tick.key }}</text>
            </g>

            <!-- ── Scatter (same axis) ── -->
            <g v-if="hasScatter">
              <line :x1="svgPadding" y1="90" :x2="svgWidth - svgPadding" y2="90" stroke="#f3f4f6" stroke-width="0.5" />
              <circle
                v-for="(point, pi) in scatter" :key="'s'+pi"
                :cx="clampX(point.value)" :cy="scatterY(pi)"
                :r="selectedDriverId === point.customerId ? 6 : 3.5"
                :fill="selectedDriverId === point.customerId ? '#FF5722' : (point.isOutlier ? '#EF4444' : '#3B82F6')"
                :opacity="selectedDriverId && selectedDriverId !== point.customerId ? 0.25 : (point.isOutlier ? 0.85 : 0.6)"
                :stroke="selectedDriverId === point.customerId ? '#fff' : 'none'"
                :stroke-width="selectedDriverId === point.customerId ? 2 : 0"
                class="dist-scatter-dot"
                @click="onScatterClick(point)"
              >
                <title>{{ point.label }} — {{ formatValue(point.value) }} {{ metricUnit }} | {{ point.sessions }} sess. | {{ Math.round(point.energy) }} kWh | {{ formatCurrency(point.cost) }}</title>
              </circle>
            </g>
          </svg>

          <!-- IQR coverage info -->
          <div class="dist-coverage-line">
            {{ $t('org-dashboard.dist-iqr-info', { iqr: formatValue(boxplot.iqr), coverage: boxplot.coveragePercent }) }}
          </div>

          <!-- Selected driver info -->
          <div v-if="selectedDriverInfo" class="dist-selected-driver">
            <v-icon x-small color="#FF5722" class="mr-1">mdi-map-marker</v-icon>
            <strong>{{ selectedDriverInfo.name }}</strong> — {{ formatValue(selectedDriverInfo.value) }} {{ metricUnit }}
            <span class="dist-selected-percentile">
              {{ $t('org-dashboard.member-fleet-percentile', { pct: selectedDriverInfo.percentile }) }}
            </span>
            <v-btn
              v-if="selectedDriverInfo.memberId"
              x-small text color="#FF5722"
              class="dist-selected-btn ml-2"
              @click="$emit('member-click', selectedDriverInfo.memberId)"
            >
              <v-icon x-small class="mr-1">mdi-account-details</v-icon>
              {{ $t('org-dashboard.view-profile') }}
            </v-btn>
          </div>

          <!-- Stats-only message -->
          <div v-if="distData.statsOnly" class="dist-banner dist-banner--muted mb-3">
            <v-icon x-small color="#6b7280" class="mr-1">mdi-chart-scatter-plot</v-icon>
            {{ $t('org-dashboard.dist-stats-only') }}
          </div>

          <!-- Histogram (aligned with boxplot axis above) -->
          <div class="dist-histogram-section" v-if="histogramBars">
            <div v-if="distData.histogram && distData.histogram.mode === 'quantile'" class="dist-quantile-label">
              <v-icon x-small color="#6b7280" class="mr-1">mdi-information-outline</v-icon>
              {{ $t('org-dashboard.dist-quantile-mode') }}
            </div>
            <svg class="dist-histogram-svg" :viewBox="`0 0 ${svgWidth} 140`" preserveAspectRatio="xMidYMid meet">
              <!-- Grid lines -->
              <line v-for="n in 3" :key="'grid'+n"
                    :x1="svgPadding" :y1="8 + 120 * n / 4"
                    :x2="svgWidth - svgPadding" :y2="8 + 120 * n / 4"
                    stroke="#f1f5f9" stroke-width="1" />
              <!-- Bars -->
              <g v-for="(bar, bi) in histogramBars" :key="'hb'+bi" class="dist-histogram-bar" @click="onHistogramClick(bi)">
                <rect :x="bar.x" :y="bar.y" :width="bar.width" :height="bar.height" rx="2" :fill="bar.color" class="dist-histogram-rect" />
                <text v-if="bar.height > 20 && bar.width > 24" :x="bar.labelX" :y="bar.y + 15"
                      text-anchor="middle" fill="white" font-size="10" font-weight="700">{{ bar.count }}</text>
                <text v-else-if="bar.count > 0 && bar.width > 16" :x="bar.labelX" :y="bar.y - 4"
                      text-anchor="middle" fill="#6b7280" font-size="9" font-weight="600">{{ bar.count }}</text>
                <title>{{ formatValue(bar.rangeStart) }} – {{ formatValue(bar.rangeEnd) }} {{ metricUnit }}: {{ bar.count }} ({{ bar.percent }}%)</title>
              </g>
              <!-- Bottom axis (thin ticks at bin boundaries — aligned with boxplot axis) -->
              <line :x1="svgPadding" :y1="130" :x2="svgWidth - svgPadding" :y2="130" stroke="#e5e7eb" stroke-width="0.5" />
              <line v-for="(tick, ti) in histogramTicks" :key="'ht'+ti"
                    :x1="tick.x" y1="130" :x2="tick.x" y2="134" stroke="#9ca3af" stroke-width="0.5" />
            </svg>
          </div>
        </div>

        <!-- Adoption gauge (bottom of distribution) -->
        <div class="adoption-section" v-if="adoptionRate != null">
          <div class="adoption-label">{{ $t('org-dashboard.adoption') }}</div>
          <div class="adoption-bar-bg">
            <div class="adoption-bar-fill" :style="{width: (adoptionRate || 0) + '%'}"></div>
          </div>
          <div class="adoption-text">
            {{ $t('org-dashboard.active-drivers', { active: activeDrivers || 0, total: totalDrivers || 0, rate: adoptionRate || 0 }) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Drill-down dialog (histogram bin detail) -->
    <v-dialog v-model="drilldown.open" max-width="700" scrollable>
      <v-card class="dist-drilldown-card">
        <v-card-title class="dist-drilldown-title">
          <v-icon small color="#8B5CF6" class="mr-2">mdi-chart-bar</v-icon>
          {{ drilldown.title }}
          <v-spacer />
          <v-btn icon small @click="drilldown.open = false"><v-icon small>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="drilldownHeaders"
            :items="drilldown.items"
            :loading="drilldown.loading"
            :server-items-length="drilldown.total"
            :options.sync="drilldownOptions"
            :footer-props="{ itemsPerPageOptions: [20] }"
            dense
            class="dist-drilldown-table"
          >
            <template #item.label="{ item }">
              <a class="dist-drilldown-link" @click.stop="$emit('member-click', item.memberId || item.customerId)">
                {{ item.label }}
              </a>
            </template>
            <template #item.energy="{ item }">
              {{ Math.round(item.energy || 0).toLocaleString() }} kWh
            </template>
            <template #item.sessions="{ item }">
              {{ item.sessions }}
            </template>
            <template #item.cost="{ item }">
              {{ formatCurrency(item.cost) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import OrganizationDashboardRepository from '@/repository/OrganizationDashboardRepository'

const METRIC_CONFIG = {
  energy:     { labelKey: 'dist-energy',        unit: 'kWh',      decimals: 1 },
  sessions:   { labelKey: 'dist-sessions',      unit: 'sessions', decimals: 0 },
  cost:       { labelKey: 'dist-cost',          unit: '€',        decimals: 2 },
  costPerKwh: { labelKey: 'dist-cost-per-kwh',  unit: '€/kWh',    decimals: 2 },
}

export default {
  name: 'DashboardDriverDistribution',
  props: {
    organizationId: { type: String, default: '' },
    period: { type: String, default: '30d' },
    scope: { type: String, default: 'self' },
    // Cost segment (all|home|enterprise|roaming) — owned by the parent, drives both widgets.
    segment: { type: String, default: 'all' },
    // Driver listing (from parent)
    drivers: { type: Array, default: () => [] },
    totalDrivers: { type: Number, default: 0 },
    activeDrivers: { type: Number, default: 0 },
    adoptionRate: { type: Number, default: 0 },
  },

  data() {
    return {
      metric: 'energy',
      driverSearch: '',
      distData: null,
      distLoading: false,
      svgWidth: 800,
      svgPadding: 40,
      selectedDriverId: null,
      drilldown: {
        open: false,
        title: '',
        items: [],
        loading: false,
        total: 0,
        rangeStart: null,
        rangeEnd: null,
      },
      drilldownPage: 1,
      drilldownSort: null,
      drilldownSortDesc: true,
    }
  },

  computed: {
    filteredDrivers() {
      let list = this.drivers || []
      if (this.driverSearch) {
        const search = this.driverSearch.toLowerCase()
        list = list.filter(d => {
          return (d.name && d.name.toLowerCase().includes(search))
              || (d.email && d.email.toLowerCase().includes(search))
        })
      }
      // Rank by the active measure (the list carries the full population, so this is the "top" for the
      // chosen metric — incl. €/kWh — without a refetch).
      return [...list].sort((a, b) => this.getDriverMetric(b) - this.getDriverMetric(a))
    },

    stats() { return this.distData?.stats || null },
    boxplot() { return this.distData?.boxplot || {} },
    scatter() { return this.distData?.scatter || [] },
    metricCfg() { return METRIC_CONFIG[this.metric] },
    metricLabel() { return this.$t('org-dashboard.' + this.metricCfg.labelKey) },
    metricUnit() {
      // formatValue already carries the unit for cost (currency) and costPerKwh (€/kWh suffix)
      return (this.metric === 'cost' || this.metric === 'costPerKwh') ? '' : this.metricCfg.unit
    },

    segmentUnit() {
      if (this.metric === 'cost') return '€'
      if (this.metric === 'costPerKwh') return '€/kWh'
      if (this.metric === 'sessions') return 'sess.'
      return 'kWh'
    },

    // Fleet average of the active metric — the same figure shown as "Moyenne" in the distribution panel
    // (per measure + segment), used for the per-driver "vs moy" so the badge is coherent with it. Falls back
    // to the visible drivers' mean only if the distribution stats have not loaded yet.
    fleetMean() {
      if (this.stats && this.stats.mean != null) {
        return this.stats.mean
      }
      const vals = (this.drivers || []).map(d => this.getDriverMetric(d)).filter(v => v > 0)
      if (!vals.length) return null
      return vals.reduce((a, b) => a + b, 0) / vals.length
    },

    // ── Left column helpers ──
    maxMetric() {
      if (!this.drivers?.length) return 1
      return Math.max(...this.drivers.map(d => this.getDriverMetric(d)), 1)
    },

    // ── Right column: distribution viz ──
    kpiItems() {
      const s = this.stats
      if (!s) return []
      return [
        { label: this.$t('org-dashboard.dist-drivers-count'), value: s.count },
        { label: this.$t('org-dashboard.dist-mean'), value: this.formatValue(s.mean) + ' ' + this.metricUnit },
        { label: this.$t('org-dashboard.dist-median'), value: this.formatValue(s.median) + ' ' + this.metricUnit },
        { label: this.$t('org-dashboard.dist-stddev'), value: this.formatValue(s.stdDev) + ' ' + this.metricUnit },
        { label: 'P90', value: this.formatValue(s.percentiles?.p90) + ' ' + this.metricUnit },
      ]
    },

    xRange() {
      const axis = this.distData?.axis
      if (!axis) return { min: 0, max: 1 }
      let min = axis.suggestedMin ?? 0
      let max = axis.suggestedMax ?? 1
      min = Math.max(0, min)
      if (max <= min) max = min + 1
      // Small margin for visual breathing room
      const margin = (max - min) * 0.03
      return { min: Math.max(0, min - margin), max: max + margin }
    },

    boxplotOutliers() {
      return this.boxplot?.outliers || []
    },

    outlierCount() {
      return this.boxplotOutliers.length
    },

    meanDiamondPoints() {
      const cx = this.xScale(this.stats?.mean ?? 0)
      const cy = 25
      const r = 5
      return `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`
    },

    // ── Selected driver ──
    selectedDriverValue() {
      if (!this.selectedDriverId) return null
      const point = this.scatter.find(p => p.customerId === this.selectedDriverId)
      if (point) return point.value
      const driver = (this.drivers || []).find(d => d.memberId === this.selectedDriverId)
      if (driver) return this.getDriverMetric(driver)
      return null
    },

    selectedDriverArrowPoints() {
      if (this.selectedDriverValue == null) return ''
      const x = this.clampX(this.selectedDriverValue)
      return `${x},2 ${x - 4},8 ${x + 4},8`
    },

    selectedDriverInfo() {
      if (!this.selectedDriverId) return null
      const driver = (this.drivers || []).find(d => d.memberId === this.selectedDriverId)
      const scatterPoint = this.scatter.find(p => p.customerId === this.selectedDriverId)
      const name = driver?.name || scatterPoint?.label || '—'
      const value = scatterPoint?.value ?? (driver ? this.getDriverMetric(driver) : null)
      if (value == null) return null
      const below = this.scatter.filter(p => p.value < value).length
      const percentile = this.scatter.length > 0 ? Math.round((below / this.scatter.length) * 100) : 0
      const memberId = driver?.memberId || scatterPoint?.memberId || scatterPoint?.customerId || null
      return { name, value, percentile, memberId }
    },

    hasScatter() {
      return !this.distData?.statsOnly && this.scatter.length > 0
    },

    combinedSvgHeight() {
      return this.hasScatter ? 140 : 90
    },

    axisTicks() {
      const bp = this.boxplot
      if (!bp || bp.whiskerLow == null) return []
      const realMin = this.stats?.min
      const realMax = this.stats?.max
      const hasLowOutliers = realMin != null && realMin < bp.whiskerLow
      const hasHighOutliers = realMax != null && realMax > bp.whiskerHigh
      const items = [
        // Position at whisker, but show real min value + label if outliers exist
        hasLowOutliers
          ? { key: 'Min', val: bp.whiskerLow, displayText: this.formatAxisLabel(realMin), valueColor: '#EF4444' }
          : { key: 'Min', val: bp.whiskerLow, valueColor: '#374151' },
        { key: 'Q1', val: bp.q1, valueColor: '#374151' },
        { key: this.$t('org-dashboard.dist-median'), val: bp.median, valueColor: '#8B5CF6' },
        { key: this.$t('org-dashboard.dist-mean'), val: this.stats?.mean, valueColor: '#3B82F6' },
        { key: 'Q3', val: bp.q3, valueColor: '#374151' },
        hasHighOutliers
          ? { key: 'Max', val: bp.whiskerHigh, displayText: this.formatAxisLabel(realMax), valueColor: '#EF4444' }
          : { key: 'Max', val: bp.whiskerHigh, valueColor: '#374151' },
      ].filter(it => it.val != null)

      // Merge items with identical or very close values (e.g. Min=0 and Q1=0)
      const range = (bp.whiskerHigh || 1) - (bp.whiskerLow || 0)
      const mergeTolerance = range * 0.01 // 1% of range
      const merged = []
      for (const it of items) {
        const existing = merged.find(m => Math.abs(m.val - it.val) <= mergeTolerance)
        if (existing) {
          existing.key += ' / ' + it.key
          // Keep the most prominent color (purple median > blue mean > gray)
          if (it.valueColor === '#8B5CF6') existing.valueColor = it.valueColor
          else if (it.valueColor === '#3B82F6' && existing.valueColor === '#374151') existing.valueColor = it.valueColor
        } else {
          merged.push({ ...it })
        }
      }

      const ticks = merged.map(it => ({
        ...it,
        text: it.displayText || this.formatAxisLabel(it.val),
        trueX: this.xScale(it.val),
        x: this.xScale(it.val),
      }))
      ticks.sort((a, b) => a.trueX - b.trueX)

      // Collision avoidance — push apart labels that overlap
      const minGap = 55
      for (let i = 1; i < ticks.length; i++) {
        if (ticks[i].x - ticks[i - 1].x < minGap) {
          ticks[i].x = ticks[i - 1].x + minGap
        }
      }
      const maxX = this.svgWidth - this.svgPadding + 10
      for (let i = ticks.length - 1; i >= 0; i--) {
        ticks[i].x = Math.min(ticks[i].x, maxX)
      }
      for (let i = ticks.length - 2; i >= 0; i--) {
        if (ticks[i + 1].x - ticks[i].x < minGap) {
          ticks[i].x = ticks[i + 1].x - minGap
        }
      }
      const minX = this.svgPadding - 10
      ticks.forEach(t => { t.x = Math.max(minX, Math.min(maxX, t.x)) })
      return ticks
    },

    histogramBars() {
      const rawBins = this.distData?.histogram?.bins
      if (!rawBins?.length) return null

      const { min: xMin, max: xMax } = this.xRange
      const maxH = 120
      const top = 8
      const gap = 1.5

      // Merge bins that start beyond xRange into the last visible bin
      const bins = []
      for (const bin of rawBins) {
        if (bin.rangeStart >= xMax && bins.length > 0) {
          // Merge into last visible bin
          const last = bins[bins.length - 1]
          last.count += bin.count
          last.percent = Math.round((last.percent + (bin.percent || 0)) * 10) / 10
          last.rangeEnd = bin.rangeEnd
        } else {
          bins.push({ ...bin })
        }
      }

      const maxCount = Math.max(...bins.map(b => b.count), 1)

      return bins.map((bin, i) => {
        const start = Math.max(bin.rangeStart, xMin)
        const end = Math.min(bin.rangeEnd, xMax)
        if (end <= start) return null

        const x1 = this.xScale(start)
        const x2 = this.xScale(end)
        const rawW = x2 - x1
        const w = Math.max(rawW - gap * 2, 4)
        const h = bin.count > 0 ? Math.max((bin.count / maxCount) * maxH, 2) : 0

        return {
          x: x1 + gap,
          y: top + maxH - h,
          width: w,
          height: h,
          labelX: x1 + rawW / 2,
          count: bin.count,
          percent: bin.percent,
          rangeStart: bin.rangeStart,
          rangeEnd: bin.rangeEnd,
          color: '#C4B5FD',
          index: i,
        }
      }).filter(Boolean)
    },

    histogramTicks() {
      const bins = this.distData?.histogram?.bins
      if (!bins?.length) return []
      const { min: xMin, max: xMax } = this.xRange
      const edges = new Set()
      edges.add(bins[0].rangeStart)
      bins.forEach(b => { if (b.rangeEnd <= xMax) edges.add(b.rangeEnd) })
      return [...edges].map(val => ({ x: this.xScale(Math.max(xMin, Math.min(xMax, val))) }))
    },

    drilldownHeaders() {
      return [
        { text: this.$t('org-dashboard.dist-driver'), value: 'label' },
        { text: this.$t('org-dashboard.dist-energy') + ' (kWh)', value: 'energy' },
        { text: this.$t('org-dashboard.dist-sessions'), value: 'sessions' },
        { text: this.$t('org-dashboard.dist-cost') + ' (€)', value: 'cost' },
      ]
    },

    drilldownOptions: {
      get() {
        return {
          page: this.drilldownPage,
          itemsPerPage: 20,
          sortBy: this.drilldownSort ? [this.drilldownSort] : [this.metric],
          sortDesc: [this.drilldownSortDesc],
        }
      },
      set(val) {
        const newSort = val.sortBy?.[0] || this.metric
        const newDesc = val.sortDesc?.[0] ?? true
        const newPage = val.page || 1
        const sortChanged = newSort !== (this.drilldownSort || this.metric) || newDesc !== this.drilldownSortDesc
        const pageChanged = newPage !== this.drilldownPage

        if (sortChanged || pageChanged) {
          this.drilldownSort = newSort
          this.drilldownSortDesc = newDesc
          this.drilldownPage = sortChanged ? 1 : newPage
          this.loadBinDrilldown(
            this.drilldown.rangeStart, this.drilldown.rangeEnd,
            this.drilldownPage, newSort, newDesc ? 'desc' : 'asc',
          )
        }
      },
    },
  },

  watch: {
    organizationId() { this.loadDistribution() },
    period() { this.loadDistribution() },
    scope() { this.loadDistribution() },
    metric(val) {
      this.$emit('update:metric', val)
      this.selectedDriverId = null
      this.loadDistribution()
    },
    segment() {
      // Parent owns the segment and the top-drivers query; the distribution is ours, so reload it
      // whenever the parent switches segment. Both widgets stay on the same scope.
      this.selectedDriverId = null
      this.drilldown.open = false
      this.loadDistribution()
    },
  },

  mounted() {
    this.loadDistribution()
  },

  methods: {
    // ── Data loading ──
    loadDistribution() {
      if (!this.organizationId) return
      this.distLoading = true
      OrganizationDashboardRepository.getDistribution(this.organizationId, {
        period: this.period,
        scope: this.scope,
        metric: this.metric,
        segment: this.segment,
      })
        .then(data => { this.distData = data })
        .catch(err => {
          this.distData = null
          this.notifyError(err)
        })
        .finally(() => { this.distLoading = false })
    },

    // ── Cross-interactions ──
    // Hover on left column → highlight in right column (scatter/boxplot)
    onDriverHover(driver) {
      if (!driver.memberId) return
      this.selectedDriverId = driver.memberId
    },

    onDriverHoverOut() {
      this.selectedDriverId = null
    },

    // Click on left column → open member drawer
    onDriverOpen(driver) {
      if (!driver.memberId) return
      this.$emit('member-click', driver.memberId)
    },

    // Click on scatter dot → highlight + scroll to driver in left column
    onScatterClick(point) {
      this.selectedDriverId = point.customerId
      this.$nextTick(() => {
        const el = document.getElementById('driver-' + point.customerId)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    },

    onHistogramClick(index) {
      const bar = this.histogramBars?.[index]
      if (!bar) return

      // Open drill-down dialog with bar data (may be merged bins)
      this.drilldown.open = true
      this.drilldown.title = `${this.formatValue(bar.rangeStart)} – ${this.formatValue(bar.rangeEnd)} ${this.metricUnit} (${bar.count} ${this.$t('org-dashboard.dist-drivers-count').toLowerCase()})`
      this.drilldown.rangeStart = bar.rangeStart
      this.drilldown.rangeEnd = bar.rangeEnd
      this.drilldownPage = 1
      this.drilldownSort = this.metric
      this.drilldownSortDesc = true
      this.loadBinDrilldown(bar.rangeStart, bar.rangeEnd, 1, this.metric, 'desc')
    },

    loadBinDrilldown(rangeStart, rangeEnd, page = 1, sortBy, sortDir) {
      this.drilldown.loading = true
      OrganizationDashboardRepository.getDistributionBin(this.organizationId, {
        period: this.period,
        scope: this.scope,
        metric: this.metric,
        rangeStart,
        rangeEnd,
        page,
        limit: 20,
        sortBy: sortBy || this.drilldownSort || this.metric,
        sortDir: sortDir || (this.drilldownSortDesc ? 'desc' : 'asc'),
        segment: this.segment,
      })
        .then(data => {
          this.drilldown.items = data.data || []
          this.drilldown.total = data.pagination?.total || 0
        })
        .catch(err => {
          this.notifyError(err)
        })
        .finally(() => { this.drilldown.loading = false })
    },

    // ── Left column helpers ──
    getDriverMetric(driver) {
      if (this.metric === 'cost') return driver.cost || 0
      if (this.metric === 'sessions') return driver.sessions || 0
      // €/kWh is a ratio — undefined without energy, so 0 (ranked last, badge/value show "—").
      if (this.metric === 'costPerKwh') return driver.energy > 0 ? driver.cost / driver.energy : 0
      return driver.energy || 0
    },

    formatMetricValue(driver) {
      const val = this.getDriverMetric(driver)
      if (this.metric === 'cost') {
        return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val)
      }
      if (this.metric === 'costPerKwh') {
        return val > 0 ? val.toLocaleString(this.$i18n.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €/kWh' : '—'
      }
      if (this.metric === 'sessions') return val + ' sess.'
      return Math.round(val) + ' kWh'
    },

    // Per-driver deviation from the fleet average of the ACTIVE measure (replaces the stale, energy-only
    // deltaVsFleetAvg from the API). Coherent with the value shown and with the panel's "Moyenne".
    deltaVsAvg(driver) {
      const mean = this.fleetMean
      if (!mean) return null
      const val = this.getDriverMetric(driver)
      if (this.metric === 'costPerKwh' && val === 0) return null
      return (val - mean) / mean * 100
    },

    getBarPercent(driver) {
      return Math.round((this.getDriverMetric(driver) / this.maxMetric) * 100)
    },

    getSegmentBars(driver) {
      const total = this.getDriverMetric(driver)
      if (!total) return []
      const barWidth = this.getBarPercent(driver)
      const colors = {
        Enterprise: '#3B82F6',
        Home: '#10B981',
        RoamingOutgoing: '#F59E0B',
      }
      return driver.segments
        .map(seg => {
          const val = this.getSegmentMetric(seg)
          if (!val) return null
          return {
            name: seg.name,
            value: this.metric === 'cost'
              ? this.formatCurrency(val)
              : (this.metric === 'sessions' ? val + ' sess.' : Math.round(val) + ' kWh'),
            percent: (val / total) * barWidth,
            color: colors[seg.name] || '#6B7280',
          }
        })
        .filter(Boolean)
    },

    getSegmentMetric(seg) {
      if (this.metric === 'cost') return seg.cost || 0
      if (this.metric === 'sessions') return seg.sessions || 0
      return seg.energy || 0
    },

    segmentLabel(name) {
      const labels = {
        Home: this.$t('org-dashboard.segment-home'),
        Enterprise: this.$t('org-dashboard.segment-enterprise'),
        RoamingOutgoing: this.$t('org-dashboard.segment-roaming'),
      }
      return labels[name] || name
    },

    formatDelta(val) {
      if (val == null) return ''
      const sign = val >= 0 ? '+' : ''
      return sign + Math.round(val) + '%'
    },

    getDeltaClass(val) {
      if (val > 0) return 'driver-badge--positive'
      if (val < 0) return 'driver-badge--negative'
      return ''
    },

    homeChargerIcon(driver) {
      const icons = {
        'charging-point': { icon: 'mdi-ev-station', color: '#10B981' },
        'mid-meter':      { icon: 'mdi-counter', color: '#8B5CF6' },
        'smart-cable':    { icon: 'mdi-cable-data', color: '#3B82F6' },
        'connected-plug': { icon: 'mdi-power-plug', color: '#F59E0B' },
        'external-operator': { icon: 'mdi-domain', color: '#EC4899' },
      }
      return icons[driver.homeChargerType] || { icon: 'mdi-home', color: '#6B7280' }
    },

    homeChargerLabel(type) {
      if (type === 'charging-point') {
        return this.$t('org-dashboard.home-charger-type.charging-point')
      }
      // Les autres types sont des types de compteur : libellés partagés dans enums.meter-device
      if (type && this.$te('enums.meter-device.' + type)) {
        return this.$t('enums.meter-device.' + type)
      }
      return this.$t('org-dashboard.home-charger-type.unknown')
    },

    // ── Right column helpers ──
    xScale(val) {
      const { min, max } = this.xRange
      const width = this.svgWidth - this.svgPadding * 2
      return this.svgPadding + ((val - min) / (max - min)) * width
    },

    // Clamp value to data boundaries (suggestedMin/Max), not the padded xRange
    clampX(val) {
      const axis = this.distData?.axis
      const max = axis?.suggestedMax ?? this.xRange.max
      const min = axis?.suggestedMin ?? this.xRange.min
      return this.xScale(Math.max(min, Math.min(max, val)))
    },

    scatterY(index) {
      const seed = ((index * 2654435761) >>> 0) / 4294967296
      return 93 + seed * 35
    },

    formatValue(val) {
      if (val == null) return '-'
      if (this.metric === 'cost') {
        return new Intl.NumberFormat(this.$i18n.locale, {
          style: 'currency', currency: 'EUR', maximumFractionDigits: this.metricCfg.decimals,
        }).format(val)
      }
      if (this.metric === 'costPerKwh') {
        return val.toLocaleString(this.$i18n.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €/kWh'
      }
      return val.toLocaleString(this.$i18n.locale, { maximumFractionDigits: this.metricCfg.decimals })
    },

    formatAxisLabel(val) {
      if (val == null) return ''
      if (this.metric === 'cost') {
        if (Math.abs(val) >= 1000) return Math.round(val / 1000) + 'k€'
        return Math.round(val) + '€'
      }
      if (this.metric === 'costPerKwh') {
        return val.toLocaleString(this.$i18n.locale, { maximumFractionDigits: 2 }) + '€'
      }
      if (Math.abs(val) >= 10000) return Math.round(val / 1000) + 'k'
      return val.toLocaleString(this.$i18n.locale, { maximumFractionDigits: this.metricCfg.decimals })
    },

    formatCurrency(val) {
      if (val == null) return '-'
      return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: 'EUR' }).format(val)
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-driver-distribution {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  position: relative;
}

/* ── Toolbar ── */
/* Filter bar at the top of the card — governs both columns. */
.dist-filterbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 32px;
  padding-bottom: 14px;
  margin-bottom: 18px;
  border-bottom: 1px solid #f3f4f6;
}

.dist-filter {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dist-filter-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
}

.dist-filter-help {
  color: #9ca3af !important;
  cursor: help;
}

.dist-toolbar {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 24px;
  align-items: center;
  margin-bottom: 20px;

  .dist-title:last-child {
    padding-left: 24px;
  }
}

.dist-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 6px;
}

.driver-search-wrapper {
  .driver-search-field {
    max-width: 180px;
    font-size: 12px;
    border-radius: 16px;
    background: #f3f4f6;
    .v-input__slot {
      min-height: 28px !important;
      padding: 0 8px !important;
    }
    .v-icon {
      font-size: 16px !important;
    }
  }
}

.dist-metric-toggle,
.dist-seg-toggle {
  &.v-btn-toggle {
    border-radius: 6px;
    background: #f3f4f6;

    .v-btn {
      height: 28px !important;
      min-width: 0 !important;
      padding: 0 14px !important;
      font-size: 11px !important;
      font-weight: 600;
      letter-spacing: 0;
      text-transform: none;
      border: none !important;

      &.v-btn--active {
        background: #fff !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        color: #8B5CF6 !important;
      }
    }
  }
}

/* ── 2-column layout ── */
.dist-columns {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 24px;
  min-height: 380px;
}

.dist-col-left {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dist-col-right {
  border-left: 1px solid #f3f4f6;
  padding-left: 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dist-chart-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}


/* ── Driver listing ── */
.driver-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  overflow-y: auto;
  max-height: 600px;
  padding-right: 4px;
}

.driver-item--clickable {
  cursor: pointer;

  &:hover {
    background: #f9fafb;
    border-radius: 8px;
  }
}

.driver-item--selected {
  background: #fef3f0 !important;
  border-radius: 8px;
  outline: 2px solid #FF5722;
  outline-offset: 2px;
}

.driver-item {
  padding: 4px 6px;
  transition: background 0.15s, outline 0.15s;
}

.driver-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.driver-item-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.driver-item-email {
  font-size: 10px;
  color: #6b7280;
}

.driver-item-metrics {
  text-align: right;
}

.driver-item-value {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
}

.driver-item-badges {
  display: flex;
  gap: 4px;
  margin-top: 2px;
  justify-content: flex-end;
}

.driver-badge {
  font-size: 9px;
  padding: 1px 5px;
  border-radius: 4px;
  font-weight: 600;
  white-space: nowrap;

  &--delta { background: #f3f4f6; color: #6b7280; }
  &--positive { background: #dcfce7; color: #16a34a; }
  &--negative { background: #fef2f2; color: #dc2626; }
  &--flag { background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa; }
}

.driver-bar-bg {
  width: 100%;
  height: 5px;
  background: #f3f4f6;
  border-radius: 99px;
  overflow: hidden;
  display: flex;
}

.driver-bar-fill {
  height: 100%;
  border-radius: 99px;
  background: #F97316;
  transition: width 0.6s ease;
}

.driver-bar-segment {
  height: 100%;
  position: relative;
  transition: width 0.6s ease;

  &:first-child {
    border-radius: 99px 0 0 99px;
  }

  &:last-child {
    border-radius: 0 99px 99px 0;
  }

  &:only-child {
    border-radius: 99px;
  }
}

.driver-bar-segment-hit {
  position: absolute;
  inset: -4px 0;
  cursor: default;
}

.driver-empty {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
  padding: 24px 0;
}

/* ── Adoption ── */
.adoption-section {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
}

.adoption-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6b7280;
  margin-bottom: 6px;
}

.adoption-bar-bg {
  width: 100%;
  height: 8px;
  background: #f3f4f6;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 4px;
}

.adoption-bar-fill {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #8B5CF6, #a78bfa);
  transition: width 0.6s ease;
}

.adoption-text {
  font-size: 11px;
  color: #4b5563;
  font-weight: 500;
}

/* ── Right column states ── */
.dist-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.dist-empty {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 40px 0;
}

.dist-banner {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  padding: 6px 12px;

  &--info { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
  &--muted { background: #f9fafb; color: #6b7280; border: 1px solid #e5e7eb; }
}

/* ── Transitions ── */
.dist-fade-enter-active, .dist-fade-leave-active {
  transition: opacity 0.25s ease;
}
.dist-fade-enter, .dist-fade-leave-to {
  opacity: 0;
}

/* ── KPI bandeau ── */
.dist-kpi-row {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.dist-kpi {
  flex: 1;
  min-width: 80px;
  background: #f9fafb;
  border-radius: 8px;
  padding: 8px 10px;
  text-align: center;
  border: 1px solid #f3f4f6;
}

.dist-kpi-value {
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
}

.dist-kpi-label {
  font-size: 8px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 1px;
}

/* ── Boxplot header ── */
.dist-boxplot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.dist-boxplot-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6b7280;
}

.dist-outlier-warning {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  color: #DC2626;
  background: #fef2f2;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #fecaca;
}

/* ── Combined SVG (boxplot + axis + scatter) ── */
.dist-combined-svg {
  width: 100%;
  height: auto;
  display: block;

  line, rect, circle, polygon {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.dist-coverage-line {
  font-size: 10px;
  color: #6b7280;
  text-align: center;
  margin-top: 2px;
  margin-bottom: 8px;
}

/* ── Selected driver info ── */
.dist-selected-driver {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #374151;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  padding: 6px 12px;
  margin-bottom: 8px;
}

.dist-selected-percentile {
  margin-left: auto;
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
}

.dist-selected-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  font-size: 11px !important;
  padding: 0 6px !important;
  min-width: 0 !important;
  height: 24px !important;
}

/* ── Scatter dots (inside combined SVG) ── */
.dist-scatter-dot {
  cursor: pointer;
  transition: opacity 0.15s, r 0.15s;

  &:hover {
    opacity: 1 !important;
  }
}

/* ── Histogram SVG ── */
.dist-histogram-svg {
  width: 100%;
  height: auto;
  display: block;
}

.dist-histogram-bar {
  cursor: pointer;
}

.dist-histogram-rect {
  transition: x 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              y 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              width 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.15s, filter 0.15s;

  &:hover {
    opacity: 0.85;
    filter: brightness(0.92);
  }
}

/* ── Histogram ── */
.dist-histogram-section {
  margin-top: 4px;
}

.dist-quantile-label {
  display: flex;
  align-items: center;
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 2px;
}

/* ── Drill-down dialog ── */
.dist-drilldown-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.dist-drilldown-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  padding: 14px 20px 10px;
  border-bottom: 1px solid #f3f4f6;
}

.dist-drilldown-link {
  color: #8B5CF6;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
    color: #7c3aed;
  }
}

.dist-drilldown-table {
  ::v-deep {
    thead th {
      font-size: 10px !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      color: #6b7280 !important;
      background: #f9fafb !important;
      height: 36px !important;
    }

    tbody td {
      font-size: 12px !important;
      height: 36px !important;
      color: #374151;
    }

    tbody tr:hover {
      background: #faf5ff !important;
    }
  }
}

/* ── Responsive ── */
@media (max-width: 1100px) {
  .dist-columns {
    grid-template-columns: 1fr;
  }

  .dist-col-right {
    border-left: none;
    border-top: 1px solid #f3f4f6;
    padding-left: 0;
    padding-top: 16px;
  }

  .driver-list {
    max-height: 300px;
  }
}
</style>
